import React, { useState, useEffect, useRef } from 'react';
import { Scene } from '../types';
import { Cuboid, Image as ImageIcon, Clock, Share2, Download, Trophy, Zap, Trash2 } from 'lucide-react';
import { S3Client, GetObjectCommand, PutObjectCommand } from '@aws-sdk/client-s3';
import { customerApi } from '../services/customerApi';
import { generateThumbnail, blobToUint8Array } from '../utils/thumbnail';
import { s3CredentialsManager } from '../services/s3CredentialsManager.service';
import logo from '../assets/logo.gif';
import { THUMBNAIL_EXPIRY_MARGIN_MS } from '../constants/upload.constants';


interface Props {
  scene: Scene;
  onClick: () => void;
  onShare?: () => void;
  onDownload?: () => void;
  onRate?: () => void;
  onDelete?: () => void;
  onTrigger?: () => void;
  canShare?: boolean; // New prop to control share button visibility based on user role
  isLoading?: boolean;
  isDisabled?: boolean; // Optional prop to disable interactions
}

export default function SceneCard({ scene, onClick, onShare, onDownload, onRate, onDelete, onTrigger, canShare = false, isLoading = false, isDisabled = false }: Props) {
  const [fallbackThumb, setFallbackThumb] = useState<string | null>(null);
  const [isThumbLoading, setIsThumbLoading] = useState(false);
  const [photoUrl, setPhotoUrl] = useState<string | null>(null);
  const [isHovered, setIsHovered] = useState(false);
  
  // Enhanced refs with session tracking
  const s3Ref = useRef<S3Client | null>(null);
  const firstImageKeyRef = useRef<string | null>(null);
  const [hasUploadedThumb, setHasUploadedThumb] = useState(false);
  const sessionFolderRef = useRef<string | null>(null);

  // ===== ENHANCED: Cleanup thumbnail session on unmount =====
    useEffect(() => {
    return () => {
      // Cleanup thumbnail session when component unmounts
      if (sessionFolderRef.current) {
        console.log(`[SceneCard] Component unmounting - completing thumbnail session for: ${sessionFolderRef.current}`);
        s3CredentialsManager.completeThumbnailSession(sessionFolderRef.current).catch(err => 
          console.warn(`[SceneCard] Failed to complete thumbnail session on unmount:`, err)
        );
      }
    };
  }, []);

    useEffect(() => {
      
      const isExpired =
      scene.thumbnailUrlExpiresAt &&
      Date.now() > new Date(scene.thumbnailUrlExpiresAt).getTime() - THUMBNAIL_EXPIRY_MARGIN_MS;

      // If we already have an official thumbnail, use it
      if (scene.thumbnailUrl && !isExpired ) {
        console.log('[SceneCard] Official thumbnail present, using it');
        setFallbackThumb(null);
        setIsThumbLoading(false);
        return;
      }

      // If no official thumbnail and we have a scene ID, try to get one from gallery service
      if (!scene.id) {
        console.log('[SceneCard] No scene ID available for thumbnail lookup');
        return;
      }

      console.log(`[SceneCard] 🖼️ Fetching thumbnail for scene: ${scene.name} (ID: ${scene.id})`);
      setIsThumbLoading(true);

      const fetchThumbnail = async () => {
        try {
          
          const thumbnailUrl = await customerApi.getThumbnailForScan(scene.id);
          
          if (thumbnailUrl) {
            console.log(`[SceneCard] ✅ Thumbnail URL retrieved from gallery service`);
            setFallbackThumb(thumbnailUrl);
          } else {
            console.log(`[SceneCard] ⚠️ No thumbnail available for scene: ${scene.name}`);
            setFallbackThumb(null);
          }
        } catch (error) {
          console.warn(`[SceneCard] Failed to fetch thumbnail for scene ${scene.name}:`, error);
          setFallbackThumb(null);
        } finally {
          setIsThumbLoading(false);
        }
      };

      fetchThumbnail();
    }, [scene.thumbnailUrl, scene.id, scene.name]);

  const handleAction = (e: React.MouseEvent, action: () => void) => {
    e.stopPropagation();
    if (isDisabled || isLoading) return;
    action();
  };

  const handleCardClick = () => {
    if (isDisabled || isLoading) return;
    onClick();
  };

  const isCompleted = scene.status === 'completed' || (scene.status as string) === 'success';
  const isUploaded = scene.status === 'uploaded';

  // Status badge color and text
  const getStatusBadge = () => {
    if (scene.status === 'completed') return { color: 'bg-green-500/80', text: 'Completed' };
    if (scene.status === 'processing') return { color: 'bg-[#0CB4E4]/80', text: 'Processing' };
    if (scene.status === 'uploaded') return { color: 'bg-[#0CB4E4]/80', text: 'Uploaded' };
    if (scene.status === 'error') return { color: 'bg-red-500/80', text: 'Error' };
    return { color: 'bg-gray-500/80', text: 'Pending' };
  };

  const badge = getStatusBadge();

  // Only show action buttons for completed or uploaded scenes
  const showActionButtons = isCompleted || isUploaded;
  
  // Always allow delete button regardless of scene status
  const showDeleteButton = canShare && onDelete && !isLoading && !isDisabled;
  
  // Only allow card to be clickable if there are action buttons available
  const isCardClickable = showActionButtons && !isLoading && !isDisabled;

  return (
    <div 
      onClick={isCardClickable ? handleCardClick : undefined}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className={`relative overflow-hidden h-[320px] shadow-lg border border-[#0CB4E4]/30 rounded-xl group ${
        !isCardClickable || isLoading || isDisabled 
          ? 'opacity-60 cursor-not-allowed' 
          : isCardClickable ? 'hover:shadow-xl cursor-pointer transform transition-all duration-300 hover:scale-[1.02]' : ''
      }`}
    >
      {isLoading && (
        <div className="absolute inset-0 bg-[#161513]/90 backdrop-blur-sm z-10 flex items-center justify-center rounded-xl">
          <div className="text-center">
            <div className="inline-block animate-spin h-12 w-12 border-b-2 border-[#0CB4E4] mb-4"></div>
            <p className="text-sm font-medium text-white">Looking up the 3D...</p>
            <p className="text-xs text-gray-300 mt-1">Please wait</p>
          </div>
        </div>
      )}

      {isDisabled && !isLoading && (
        <div className="absolute inset-0 bg-gray-500/20 z-10 rounded-xl" />
      )}

      {/* Main Image Container */}
      <div className="absolute inset-0 w-full h-full bg-[#161513] overflow-hidden rounded-xl">
        {scene.thumbnailUrl || fallbackThumb ? (
          <img
            src={scene.thumbnailUrl || fallbackThumb!}
            alt={scene.name}
            onError={async () => {
              // Show full-resolution image as immediate fallback
              if (photoUrl && fallbackThumb !== photoUrl) {
                console.warn('[SceneCard] Thumb failed, falling back to full-res');
                setFallbackThumb(photoUrl);
              }

              // ===== ENHANCED: Thumbnail upload with session management =====
              if (
                !hasUploadedThumb &&
                photoUrl &&
                s3Ref.current &&
                firstImageKeyRef.current &&
                sessionFolderRef.current
              ) {
                try {
                  console.log('[SceneCard] 🖼️ Generating and uploading missing thumbnail with session management...');

                  // 1. Download the full-resolution image as a blob
                  const resp = await fetch(photoUrl);
                  const fullBlob = await resp.blob();

                  // 2. Generate a downscaled thumbnail, rotating left if captured on smartphone
                  const rotateLeft = scene.cameraType === 'smartphone';
                  const thumbBlob = await generateThumbnail(fullBlob, 400, 0.7, rotateLeft);

                  // 4. Convert to Uint8Array for S3 upload
                  const thumbArray = await blobToUint8Array(thumbBlob);

                  // 5. Build destination key (thumbKey already contains full folder path)
                  const thumbKey = firstImageKeyRef.current.replace('photo_', 'thumb_');
                  const key = thumbKey;

                  // 6. Upload to S3 using existing session
                  const putCommand = new PutObjectCommand({
                    Bucket: 'ar-trial', // TODO: Get from credentials
                    Key: thumbKey,
                    Body: thumbArray,
                    ContentType: 'image/jpeg',
                    Metadata: {
                      thumbnail: 'true',
                      'source-photo': firstImageKeyRef.current,
                      'session-managed': 'true'
                    }
                  });

                  await s3Ref.current.send(putCommand);
                  console.log('[SceneCard] ✅ Thumbnail uploaded successfully with session management');
                  setHasUploadedThumb(true);

                  // Note: We don't complete the session here as the component might still need it
                  
                } catch (uploadErr) {
                  console.error('[SceneCard] Failed to auto-upload thumbnail:', uploadErr);
                  setHasUploadedThumb(true); // avoid retry loops
                }
              }
            }}
            className="w-full h-full object-cover transition-transform duration-500 ease-out transform-origin-center group-hover:scale-110 rounded-xl"
            style={{ transformOrigin: 'center' }}
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center">
            {isThumbLoading ? (
              <div className="animate-spin h-8 w-8 border-b-2 border-[#0CB4E4]" />
            ) : (
              <img 
                src={logo}
                alt="Scene thumbnail" 
                className="w-24 h-24 object-contain" 
              />
            )}
          </div>
        )}
        
        {/* Gradient overlay */}
        <div className={`absolute inset-0 bg-gradient-to-t from-[#161513] via-[#161513]/70 to-transparent transition-opacity duration-300 rounded-xl ${isHovered ? 'opacity-90' : 'opacity-80'}`}></div>
      </div>

      {/* Status Badge */}
      <div className="absolute top-3 right-3 z-10">
        <div className={`px-2 py-1 text-[10px] font-medium text-white ${badge.color} backdrop-blur-sm border border-white/20 rounded-md`}>
          {badge.text}
        </div>
      </div>

      {/* Image count badge */}
      <div className="absolute top-3 left-3 z-10">
        <div className="flex items-center gap-1 px-2 py-1 text-[10px] font-medium text-white bg-black/50 backdrop-blur-sm border border-white/20 rounded-md">
          <ImageIcon className="w-3 h-3" />
          <span>{scene.quality.imageCount}</span>
        </div>
      </div>

      {/* Content overlay - With conditional padding based on showActionButtons */}
      <div className={`absolute bottom-0 left-0 right-0 p-3 z-10 transition-all duration-300 ${
        isHovered ? 'translate-y-[-40px]' : 'translate-y-0'
      }`}>
        {/* Title and date */}
        <h3 className="font-semibold text-white text-base mb-1 line-clamp-2">
          {scene.name}
        </h3>
        
        <div className="flex items-center gap-1 text-[10px] text-gray-300 mb-2">
          <Clock className="w-3 h-3" />
          <span>{new Date(scene.createdAt).toLocaleDateString()}</span>
        </div>
        
        {scene.ownerUsername && (
          <div className="text-[10px] text-gray-300 mb-2">
            Creator: {scene.ownerUsername}
          </div>
        )}
        
        {/* Quality score */}
        {scene.quality.overall > 0 && (
          <div className="mb-3 transition-opacity duration-300 bg-[#161513]/80 p-2 rounded-lg">
            <div className="flex items-center justify-between text-[10px] mb-1">
              <span className="text-gray-300">Quality Score</span>
              <span className={`font-medium ${
                scene.quality.overall >= 80 ? 'text-green-400' :
                scene.quality.overall >= 60 ? 'text-[#0CB4E4]' :
                'text-red-400'
              }`}>
                {scene.quality.overall}%
              </span>
            </div>
            <div className="w-full bg-[#161513] h-1 rounded-full">
              <div
                className={`h-1 rounded-full transition-all duration-500 ${
                  scene.quality.overall >= 80 ? 'bg-green-400' :
                  scene.quality.overall >= 60 ? 'bg-[#0CB4E4]' :
                  'bg-red-400'
                }`}
                style={{ width: `${scene.quality.overall}%` }}
              />
            </div>
          </div>
        )}
      </div>
        
      {/* Action buttons - For completed/uploaded scenes or just delete */}
      {(showActionButtons || showDeleteButton) && (
        <div 
          className={`absolute left-0 right-0 p-1 flex gap-1 bg-[#161513]/90 backdrop-blur-sm z-20 border-t border-[#0CB4E4]/20 transition-all duration-300 ${
            isHovered ? 'bottom-0' : 'bottom-[-52px]' 
          }`}
        >
          {isCompleted && (
            <>
              {canShare && (
                <button
                  onClick={(e) => handleAction(e, onShare || (() => {}))}
                  className={`flex items-center justify-center gap-1 py-0.5 px-2 text-xs font-medium flex-1 rounded-lg h-7 ${
                    !isLoading && !isDisabled
                      ? 'bg-[#0CB4E4]/90 text-white hover:bg-[#0C5484] shadow-sm' 
                      : 'bg-gray-700/50 text-gray-400 cursor-not-allowed'
                  }`}
                  title={isCompleted && !isDisabled && !isLoading ? 'Share this scene with others' : 'Scene must be completed to share'}
                >
                  <Share2 className="w-3 h-3" />
                  <span>Share</span>
                </button>
              )}
              <button
                onClick={(e) => handleAction(e, onDownload || (() => {}))}
                className={`flex items-center justify-center gap-1 py-0.5 px-2 text-xs font-medium flex-1 rounded-lg h-7 ${
                  !isLoading && !isDisabled
                    ? 'bg-[#0CB4E4]/90 text-white hover:bg-[#0C5484] shadow-sm' 
                    : 'bg-gray-700/50 text-gray-400 cursor-not-allowed'
                }`}
                title={isCompleted && !isDisabled && !isLoading ? 'Download scene data' : 'Scene must be completed to download'}
              >
                <Download className="w-3 h-3" />
                <span>Download</span>
              </button>
            </>
          )}

          {isUploaded && (
            <button
              onClick={(e) => handleAction(e, onTrigger || (() => {}))}
              className={`flex items-center justify-center gap-1 py-0.5 px-2 text-xs font-medium flex-1 rounded-lg h-7 ${
                !isLoading && !isDisabled
                  ? 'bg-[#0CB4E4] text-[#161513] hover:bg-[#0CB4E4]/90 shadow-sm' 
                  : 'bg-gray-700/50 text-gray-400 cursor-not-allowed'
              }`}
              disabled={isLoading || isDisabled}
              title={!isLoading && !isDisabled ? 'Trigger 3D reconstruction' : 'Action not available'}
            >
              <Zap className="w-3 h-3" />
              <span>Trigger</span>
            </button>
          )}
           
           {/* Delete button */}
           {showDeleteButton && (
             <button
               onClick={(e) => handleAction(e, onDelete || (() => {}))} 
               className={`flex items-center justify-center py-0.5 text-xs font-medium rounded-lg ${
                 !showActionButtons ? 'w-full' : 'w-8 ml-auto'
               } h-7 ${
                 !isLoading && !isDisabled
                   ? 'bg-[#808080] text-white hover:bg-[#808080]/90 shadow-sm' 
                   : 'bg-gray-700/50 text-gray-400 cursor-not-allowed'
               }`}
               disabled={isLoading || isDisabled}
               title="Delete this scene"
             >
               {!showActionButtons ? (
                 <div className="flex items-center justify-center gap-1">
                   <Trash2 className="w-3.5 h-3.5" />
                   <span>Delete</span>
                 </div>
               ) : (
                 <Trash2 className="w-3.5 h-3.5" />
               )}
             </button>
           )}
        </div>
      )}
    </div>
  );
}
